import React, { useEffect, useState } from 'react';
import { Box, Paper, Grid, Stack, Typography, FormControl, InputLabel, Select, MenuItem, Button, TextField } from '@mui/material';
import { Add } from '@mui/icons-material';
import axios from 'axios';
import '../../App.css';
import { getYearList } from '../../services/yearList';
import { getMonthList } from '../../services/monthList';
import SelectApprenticeEmployeeTable from '../table-data/SelectApprenticeEmployeeTable';
import StipendPayrollResultsTable from '../table-data/StipendPayrollResultsTable';
import Popup from '../Popup';
import Notification from '../Notification';

const yearOptions = getYearList();
const monthOptions = getMonthList();

function StipendPayroll() {
  const today = new Date();
  const currentYear = today.getFullYear().toString();
  const currentMonthIndex = today.getMonth();
  const currentMonthName = monthOptions[currentMonthIndex].title;

  const [month, setMonth] = useState(currentMonthName);
  const [year, setYear] = useState(currentYear);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [payrollEmployees, setPayrollEmployees] = useState([]);
  const [daysErrors, setDaysErrors] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState({ total_days: null });
  const [openResultsPopup, setOpenResultsPopup] = useState(false);
  const [hasProcessedStipend, setHasProcessedStipend] = useState(false);
  const [hasGeneratedStipendslip, setHasGeneratedStipendslip] = useState(false);

  const handleSelectionChange = (employees) => {
    setSelectedEmployees(employees);
  };

  const selectedEmployeeIdsKey = JSON.stringify(
    selectedEmployees.map((e) => e.employee_id).sort((a, b) => a - b)
  );

  const getMonthIndex = (monthTitle) => {
    const idx = monthOptions.findIndex((m) => m.title === monthTitle);
    return idx >= 0 ? idx : null;
  };

  const getDaysInSelectedMonth = () => {
    const yearNum = Number(year);
    const monthIdx = getMonthIndex(month);
    if (!Number.isFinite(yearNum) || monthIdx == null) return null;
    return new Date(yearNum, monthIdx + 1, 0).getDate();
  };

  // Changing month/year/selection requires re-processing
  useEffect(() => {
    setHasProcessedStipend(false);
    setHasGeneratedStipendslip(false);
  }, [month, year, selectedEmployeeIdsKey]);

  const openCreatePayrollModal = () => {
    const initial = selectedEmployees.map((emp) => ({
      employee_id: emp.employee_id,
      name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
      total_worked_days: '',
    }));
    setPayrollEmployees(initial);
    setDaysErrors({});
    setOpenCreatePopup(true);
  };

  const validateDaysValue = (value) => {
    const num = Number(value);
    if (!value || !Number.isFinite(num) || num <= 0) {
      return 'Enter valid days worked.';
    }
    const maxDays = getDaysInSelectedMonth();
    if (maxDays && num > maxDays) {
      return `Max ${maxDays} for ${month} ${year}.`;
    }
    return '';
  };

  const handleDaysChange = (index, value) => {
    // allow decimals like 25.5 (only one dot)
    const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    const newValue = parts.length <= 1 ? cleaned : `${parts[0]}.${parts.slice(1).join('')}`;
    setPayrollEmployees((prev) =>
      prev.map((emp, i) =>
        i === index ? { ...emp, total_worked_days: newValue } : emp
      )
    );
    setDaysErrors((prev) => ({
      ...prev,
      [payrollEmployees[index]?.employee_id]: validateDaysValue(newValue),
    }));
  };

  const handleCreatePayroll = async () => {
    if (!month || !year) {
      setNotify({
        isOpen: true,
        message: 'Please select month and year.',
        type: 'error',
      });
      return;
    }

    const maxDays = getDaysInSelectedMonth();
    if (!maxDays) {
      setNotify({
        isOpen: true,
        message: 'Invalid month/year selected.',
        type: 'error',
      });
      return;
    }

    const nextErrors = {};
    payrollEmployees.forEach((emp) => {
      nextErrors[emp.employee_id] = validateDaysValue(emp.total_worked_days);
    });
    setDaysErrors(nextErrors);
    const hasAnyError = Object.values(nextErrors).some((msg) => !!msg);
    if (hasAnyError) return;

    const payload = {
      month,
      year: Number(year),
      employees: payrollEmployees.map((emp) => ({
        employee_id: emp.employee_id,
        total_worked_days: Number(emp.total_worked_days),
      })),
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/create-payroll`,
        payload
      );
      if (response.status === 200) {
        const data = response.data || {};
        setResults(data.results || []);
        setSummary({ total_days: data.total_days });
        setNotify({
          isOpen: true,
          message: response.data?.message || 'Stipend payroll created successfully',
          type: 'success',
        });
        setOpenCreatePopup(false);
        setOpenResultsPopup(true);
        setHasProcessedStipend(true);
        setHasGeneratedStipendslip(false);
      }
    } catch (e) {
      setNotify({
        isOpen: true,
        message: e.response?.data?.message || 'Failed to create stipend payroll',
        type: 'error',
      });
    }
  };

  const getSelectedEmployeeIds = () =>
    selectedEmployees.map((e) => e.employee_id);

  const handleGeneratePayslips = async () => {
    if (!month || !year || selectedEmployees.length === 0) {
      setNotify({
        isOpen: true,
        message: 'Please select month, year and employees first.',
        type: 'error',
      });
      return;
    }

    const payload = {
      month,
      year: Number(year),
      employee_ids: getSelectedEmployeeIds(),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/generate-payslips`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 200) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Stipend Payslips ${month.substring(0, 3)} ${year}.zip`;
        a.click();
        setNotify({
          isOpen: true,
          message: 'Stipend payslips generated successfully',
          type: 'success',
        });
        setHasGeneratedStipendslip(true);
      } else if (response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
      } else {
        const json = await response.json().catch(() => ({}));
        setNotify({
          isOpen: true,
          message: json.message || 'Failed to generate stipend payslips',
          type: 'error',
        });
      }
    } catch (e) {
      setNotify({
        isOpen: true,
        message: 'Network error while generating stipend payslips',
        type: 'error',
      });
    }
  };

  const handleSendPayslips = async () => {
    if (!month || !year || selectedEmployees.length === 0) {
      setNotify({
        isOpen: true,
        message: 'Please select month, year and employees first',
        type: 'error',
      });
      return;
    }

    const payload = {
      month,
      year: Number(year),
      employee_ids: getSelectedEmployeeIds(),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/send-payslips`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const json = await response.json().catch(() => ({}));
      if (response.status === 200) {
        setNotify({
          isOpen: true,
          message: json.message || 'Stipend payslips sent successfully',
          type: 'success',
        });
      } else if (response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
      } else {
        setNotify({
          isOpen: true,
          message: json.message || 'Failed to send stipend payslips',
          type: 'error',
        });
      }
    } catch (e) {
      setNotify({
        isOpen: true,
        message: 'Network error while sending stipend payslips',
        type: 'error',
      });
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        margin: '4px 6px',
        padding: '4px 6px',
        fontWeight: '500px',
      }}
    >
      <Paper elevation={4}>
        <Grid className="form-title" container spacing={1} sx={{ py: 0.5, px: 1.5 }}>
          <Grid item>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
              Process Stipend 
            </Typography>
          </Grid>
        </Grid>
        <Stack spacing={1} sx={{ margin: '6px', marginTop: '0px' }}>
          <Stack
            spacing={1.5}
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Select Month</InputLabel>
              <Select
                label="Select Month"
                name="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {monthOptions.map((item) => (
                  <MenuItem key={item.id} value={item.title}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Select Year</InputLabel>
              <Select
                label="Select Year"
                name="year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {yearOptions.map((item) => (
                  <MenuItem key={item.id} value={item.title}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </Stack>

          <SelectApprenticeEmployeeTable onSelectionChange={handleSelectionChange} />

          <Grid container className="space-around" sx={{ py: 0.5 }} spacing={1}>
            <Grid item>
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={!month || !year || selectedEmployees.length === 0}
                onClick={openCreatePayrollModal}
                sx={{ minHeight: 32, textTransform: 'none' }}
              >
                Process Stipend
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={!hasProcessedStipend || !month || !year || selectedEmployees.length === 0}
                onClick={handleGeneratePayslips}
                sx={{ minHeight: 32, textTransform: 'none' }}
              >
                Generate Stipendslip 
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={!hasGeneratedStipendslip || !month || !year || selectedEmployees.length === 0}
                onClick={handleSendPayslips}
                sx={{ minHeight: 32, textTransform: 'none' }}
              >
                Send Stipendslip
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled
                sx={{ minHeight: 32, textTransform: 'none' }}
              >
                Send Mail
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Paper>
      <Popup
        title="Process Stipend"
        openPopup={openCreatePopup}
        setOpenPopup={setOpenCreatePopup}
      >
        <Box sx={{ padding: 2, minWidth: 400 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Month: {month} &nbsp;&nbsp; Year: {year}
          </Typography>
          <Stack spacing={2}>
            {payrollEmployees.map((emp, index) => (
              <Stack key={emp.employee_id} direction="row" spacing={2} alignItems="center">
                <Typography sx={{ minWidth: 200 }}>{emp.name}</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  label="Days Worked"
                  type="number"
                  value={emp.total_worked_days}
                  onChange={(e) => handleDaysChange(index, e.target.value)}
                  sx={{ width: 140 }}
                  inputProps={{ step: '0.1', min: 0 }}
                  error={!!daysErrors[emp.employee_id]}
                  helperText={daysErrors[emp.employee_id] || ''}
                  FormHelperTextProps={{
                    sx: {
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'clip',
                    },
                  }}
                />
              </Stack>
            ))}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => setOpenCreatePopup(false)}>
                Cancel
              </Button>
              <Button variant="contained" color="success" onClick={handleCreatePayroll}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popup>
      <Popup
        title={`Processed Stipend - ${month} ${year}`}
        openPopup={openResultsPopup}
        setOpenPopup={setOpenResultsPopup}
        sx={{
          '& .MuiPaper-root': {
            maxHeight: '600px',
            overflow: 'hidden',
            width: '800px',
          },
        }}
      >
        <Box
          sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            overflowX: 'auto',
          }}
        >
          <StipendPayrollResultsTable results={results} totalDays={summary.total_days} />
        </Box>
      </Popup>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default StipendPayroll;

